import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

// Keys start with the table's root key (["events"], ["requests"], ["circles"])
// so existing moderation invalidations refresh these counters automatically.
async function countPending(table: "events" | "requests" | "circles", status: string) {
  const { count, error } = await supabase
    .from(table)
    .select("id", { count: "exact", head: true })
    .eq("status", status);
  if (error) throw error;
  return count ?? 0;
}

export function useModerationCounts() {
  const { user } = useAuth();

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", user.id)
        .maybeSingle();
      return Boolean(data?.is_admin);
    },
    enabled: !!user,
  });

  const opts = { enabled: !!isAdmin, staleTime: 60_000, refetchOnWindowFocus: true } as const;

  const events = useQuery({
    queryKey: ["events", "moderation-count"],
    queryFn: () => countPending("events", "pending"),
    ...opts,
  });
  const requests = useQuery({
    queryKey: ["requests", "moderation-count"],
    queryFn: () => countPending("requests", "pending_moderation"),
    ...opts,
  });
  const circles = useQuery({
    queryKey: ["circles", "moderation-count"],
    queryFn: () => countPending("circles", "pending"),
    ...opts,
  });

  if (!isAdmin) return { events: 0, requests: 0, circles: 0, total: 0, isAdmin: false };
  const e = events.data ?? 0;
  const r = requests.data ?? 0;
  const c = circles.data ?? 0;
  return { events: e, requests: r, circles: c, total: e + r + c, isAdmin: true };
}
